import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorService } from '../../../../core/services/error.service';
import { CampaignService } from '../../../../core/services/api/campaign.service';
import { InvestmentInfo } from '../../../../core/models/investment/investment-info';
import { InvestmentAmountSelection } from './investment-amount-picker/investment-amount-picker.component';
import { BlockUiService } from '../../../../shared/services/blockUi/block-ui.service';
import { finalize } from 'rxjs/operators';
import { DefaultLogo, MaxReferalCodeLength } from '../../../../core/services/constant';
import { Campaign } from '../../../../core/models/campaign/campaign';
import { PopupService } from '../../../../shared/services/popup/popup.service';
import { GtmDataLayerService } from '../../../../core/services/gtm-data-layer.service';
import { InvestmentLimitPopupService } from '../../../../shared/services/investment-limit-popup/investment-limit-popup.service';
import { ShareType } from '../../../../core/enum/share-type.enum';

@Component({
    selector: 'app-invest-now',
    templateUrl: './invest-now.component.html',
    styleUrls: ['./invest-now.component.scss'],
    standalone: false
})
export class InvestNowComponent implements OnInit {
  slug: string;
  form: UntypedFormGroup;
  showFormError: boolean = false;
  campaign: Campaign;
  investmentInfo: InvestmentInfo;
  defaultLogo = DefaultLogo;
  MaxReferalCodeLength = MaxReferalCodeLength;

  ShareType: typeof ShareType = ShareType;
  shareTypes = [ShareType.OS, ShareType.Rps];
  isOpen = false;

  // INC-238 — the picker replaces the old lotAmount form control + confirm-invest modal; its
  // last emitted selection is what submit()/invest() act on.
  amountSelection: InvestmentAmountSelection = {
    lotQuantity: 0,
    shareQuantity: 0,
    investmentAmount: 0,
    isValid: false,
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private campaignService: CampaignService,
    private errorService: ErrorService,
    private router: Router,
    private popupService: PopupService,
    private blockUiService: BlockUiService,
    private gtmDataLayerService: GtmDataLayerService,
    private investmentLimitPopupService: InvestmentLimitPopupService,
  ) {}

  ngOnInit() {
    this.slug = this.activatedRoute.snapshot.params.slug;
    this.getCampaign();
  }

  getCampaign() {
    this.campaignService.getCampaign(this.slug).subscribe({
      next: (data) => {
        // PLT-667 Redirect to campaign details if is private campaign and is not invited
        if (data?.isPrivate && !data?.isInvited) {
          this.router.navigate([`/equity/${data?.slug}`], {
            replaceUrl: true,
          });
          return;
        }

        this.campaign = data;
        this.getCampaignInvestmentInfo();
        this.buildForm();
        this.updateHighRiskValidators(); // <-- Call after form is built
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  getCampaignInvestmentInfo() {
    this.campaignService.getCampaignInvestmentInfo(this.campaign.id).subscribe({
      next: (data) => {
        this.investmentInfo = data;
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  buildForm() {
    this.form = this.formBuilder.group({
      referralCode: [null, Validators.maxLength(MaxReferalCodeLength)],
      isAcceptTerm1: [false, Validators.required],
      isAcceptTerm2: [false, Validators.required],
      isHighRiskTerm1: [false, Validators.required],
      isHighRiskTerm2: [false, Validators.required],
      shareType: [
        this.campaign?.shareTypes?.[0],
        this.campaign?.shareTypes?.length > 1 ? Validators.required : null,
      ],
    });
  }

  updateHighRiskValidators() {
    if (this.slug === 'sky-realm') {
      this.form.get('isHighRiskTerm1')?.setValidators([Validators.requiredTrue]);
      this.form.get('isHighRiskTerm2')?.setValidators([Validators.requiredTrue]);
      this.form.get('isHighRiskTerm1')?.enable();
      this.form.get('isHighRiskTerm2')?.enable();
    } else {
      this.form.get('isHighRiskTerm1')?.clearValidators();
      this.form.get('isHighRiskTerm2')?.clearValidators();
      this.form.get('isHighRiskTerm1')?.setValue(false);
      this.form.get('isHighRiskTerm2')?.setValue(false);
      this.form.get('isHighRiskTerm1')?.disable();
      this.form.get('isHighRiskTerm2')?.disable();
    }
    this.form.get('isHighRiskTerm1')?.updateValueAndValidity();
    this.form.get('isHighRiskTerm2')?.updateValueAndValidity();
  }

  onAmountChange(selection: InvestmentAmountSelection) {
    this.amountSelection = selection;
  }

  submit() {
    if (this.form.value.isAcceptTerm1 == false || this.form.value.isAcceptTerm2 == false) {
      this.popupService.alert('common.acceptT&C');
      return;
    }

    // Show popup for high risk terms if needed
    if (
      this.slug === 'sky-realm' &&
      (this.form.get('isHighRiskTerm1')?.value === false ||
        this.form.get('isHighRiskTerm2')?.value === false)
    ) {
      this.popupService.alert('common.acceptT&C');
    }
    if (this.form.invalid || !this.amountSelection.isValid) {
      this.showFormError = true;

      return;
    }

    this.invest();
  }

  invest(isContinue: boolean = false) {
    this.blockUiService.open();

    this.campaignService
      .invest(
        this.campaign.id,
        this.amountSelection.lotQuantity,
        this.form.value.referralCode,
        isContinue,
        this.form.value.shareType,
      )
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: (data) => {
          this.router.navigate(['payment', data.result.id], { relativeTo: this.activatedRoute });
        },
        error: (err) => {
          if (
            err &&
            err.error &&
            (err.error.code == 'ERR_INVESTMENT_LIMIT_CAMPAIGN' ||
              err.error.code == 'ERR_INVESTMENT_LIMIT_INTERVAL')
          ) {
            this.logErrorMessage(err.error.code);
            this.investmentLimitPopupService.displayExceedInvestmentLimitWarning(
              err.error.message,
              err.error.code === 'ERR_INVESTMENT_LIMIT_CAMPAIGN' ? 'campaign' : 'interval',
              () => {
                this.invest(true);
              },
            );
          } else this.errorService.showError(err);
        },
      });
  }

  logErrorMessage(errorCode: string) {
    if (!errorCode) return;

    this.gtmDataLayerService.logErrorMessage(errorCode);
  }

  openImage() {
    this.isOpen = true;
  }

  closeImage() {
    this.isOpen = false;
  }
}
